/**
 * Zod boundary schemas + response types for Staffing Concerns (RP-34).
 */

import { z } from "zod";

export const PLAN_CONCERN_TYPES = [
  "NoAvailableResource",
  "SkillGap",
  "StartDate",
  "OverBudgetForStaffing",
  "Other",
] as const;
export type PlanConcernType = (typeof PLAN_CONCERN_TYPES)[number];

export const PLAN_CONCERN_STATUSES = ["Open", "Acknowledged", "Resolved"] as const;
export type PlanConcernStatus = (typeof PLAN_CONCERN_STATUSES)[number];

/** How each type reads in the UI and in notices. */
export const PLAN_CONCERN_TYPE_LABELS: Record<PlanConcernType, string> = {
  NoAvailableResource: "No available Resource",
  SkillGap: "Skill gap",
  StartDate: "Start date",
  OverBudgetForStaffing: "Over budget for staffing",
  Other: "Other",
};

export const raisePlanConcernSchema = z.object({
  type: z.enum(PLAN_CONCERN_TYPES),
  message: z
    .string()
    .trim()
    .min(1, "a Concern needs a message the account team can act on")
    .max(2000),
  /** Optional: the Plan Line the Concern is about. Null or absent = the whole plan. */
  planLineId: z.string().min(1).nullable().optional(),
  /** Blocking returns the plan to its author (the shipped Reject) — RM / ADMIN only. */
  isBlocking: z.boolean().default(false),
});

export const planConcernNoteSchema = z.object({
  note: z
    .string()
    .trim()
    .max(2000)
    .optional()
    .transform((v) => (v === undefined || v === "" ? null : v)),
});

export interface PlanConcernView {
  id: string;
  type: PlanConcernType;
  message: string;
  isBlocking: boolean;
  status: PlanConcernStatus;
  planLineId: string | null;
  planLineLabel: string | null;
  raisedByName: string | null;
  raisedAt: string;
  acknowledgedByName: string | null;
  acknowledgedAt: string | null;
  acknowledgementNote: string | null;
  resolvedByName: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  /** This is the Concern that sent the plan back to its author, and it still is. */
  returnedPlan: boolean;
  canAcknowledge: boolean;
  canResolve: boolean;
}

export interface PlanConcernListResponse {
  concerns: PlanConcernView[];
  /** May raise a Concern on this plan. */
  canRaise: boolean;
  /** May mark it Blocking — the plan is in a state that can be returned, and the actor may. */
  canRaiseBlocking: boolean;
}

/** Open-Concern counts carried on plan payloads for badges. */
export interface PlanConcernSummary {
  /** Not yet Resolved (Open or Acknowledged). */
  unresolved: number;
  /** Blocking and still Open — what stops the plan being marked Staffed. */
  blockingOpen: number;
}

/** RP-34.1 — who to ask about a plan. */
export interface PlanAccountTeamMember {
  name: string;
  email: string;
}

export interface PlanAccountTeamView {
  /** The plan's Author (owner). */
  author: PlanAccountTeamMember | null;
  /** Users who own the plan's Account — the people who acknowledge a Blocking Concern. */
  accountOwners: PlanAccountTeamMember[];
  /** CRM contacts, synced from NOS as names only — no email or Slack until NOS sends ids. */
  crmCsmName: string | null;
  crmClientPartnerName: string | null;
}
